/* Read only the last verified Shopify aggregate. No invented sales, credentials,
 * or customer/order data. The backend snapshot must be updated separately. */
(() => {
  if (customElements.get('eos-stories-counter')) return;
  customElements.define('eos-stories-counter', class extends HTMLElement {
    connectedCallback() {
      if (this._events) return;
      this._number = this.querySelector('[data-eos-story-number]');
      this._digits = this.querySelector('[data-eos-story-digits]');
      this._value = this.querySelector('[data-eos-story-value]');
      this._time = this.querySelector('[data-eos-story-time]');
      if (!this._number || !this._digits || !this._value || !this._time) return;
      this._events = new AbortController();
      document.addEventListener('visibilitychange', () => {
        clearTimeout(this._timer);
        if (document.hidden) this._request?.abort();
        else this.refresh();
      }, {signal: this._events.signal});
      this.schedule();
    }
    disconnectedCallback() {
      clearTimeout(this._timer);
      this._request?.abort();
      this._events?.abort();
      this._events = null;
    }
    schedule() {
      clearTimeout(this._timer);
      if (this.isConnected && !document.hidden && this._events) this._timer = setTimeout(() => this.refresh(), 60000);
    }
    renderDigits(display, lower) {
      const before = this._digits.textContent.replace(/\s/g, '');
      const next = display + (lower ? '+' : '');
      const fragment = document.createDocumentFragment();
      const animate = !matchMedia('(prefers-reduced-motion: reduce)').matches;
      const changedDigits = [];
      Array.from(next).forEach((character, index) => {
        const span = document.createElement('span');
        span.className = /\d/.test(character) ? 'eos-story-digit' : character === '+' ? 'eos-story-plus' : 'eos-story-separator';
        span.textContent = character;
        fragment.append(span);
        const previousIndex = index + before.length - next.length;
        if (animate && /\d/.test(character) && before[previousIndex] !== character) changedDigits.push(span);
      });
      this._digits.replaceChildren(fragment);
      this._value.textContent = (lower ? 'At least ' : '') + display;
      const bounds = this._digits.getBoundingClientRect();
      if (bounds.bottom > 0 && bounds.top < innerHeight) {
        changedDigits.forEach(span => {
          if (span.animate) span.animate([{transform:'perspective(160px) rotateX(-12deg)',opacity:.75},{transform:'perspective(160px) rotateX(0)',opacity:1}],{duration:280,easing:'ease-out'});
        });
      }
    }
    applySnapshot(next) {
      if (!next || !/^\d+$/.test(next.dataset.count || '')) throw new Error('Invalid counter');
      const count = Number(next.dataset.count);
      const asOf = Date.parse(next.dataset.asOf || '');
      const previousAsOf = Date.parse(this.dataset.asOf || '');
      if (!Number.isSafeInteger(count) || count <= 0 || count > 100000000 || !Number.isFinite(asOf)) throw new Error('Invalid aggregate');
      if (Number.isFinite(previousAsOf) && asOf < previousAsOf) return;
      if (!this.isConnected) return;
      const lower = next.dataset.lowerBound === 'true';
      const display = new Intl.NumberFormat('en-US').format(count);
      const changed = count !== Number(this.dataset.count) || lower !== (this.dataset.lowerBound === 'true');
      if (changed) this.renderDigits(display, lower);
      this.dataset.count = String(count);
      this.dataset.countLabel = display;
      this.dataset.lowerBound = String(lower);
      this.dataset.asOf = next.dataset.asOf;
      this._time.dateTime = next.dataset.asOf;
      this._time.textContent = new Intl.DateTimeFormat(document.documentElement.lang || 'en-US',{dateStyle:'medium',timeStyle:'short'}).format(new Date(asOf));
    }
    async refresh() {
      if (!this.isConnected || document.hidden || this._busy || !this._number) return;
      this._busy = true;
      const controller = this._request = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 8000);
      try {
        const url = new URL(this.dataset.root || '/', location.origin);
        if (url.origin !== location.origin) throw new Error('Invalid origin');
        url.search = '';
        url.searchParams.set('section_id', 'eos-stories-counter');
        const preview = new URL(location.href).searchParams.get('preview_theme_id');
        if (preview && /^\d+$/.test(preview)) url.searchParams.set('preview_theme_id', preview);
        const response = await fetch(url.pathname + url.search,{credentials:'same-origin',cache:'no-store',signal:controller.signal,headers:{Accept:'text/html'}});
        if (!response.ok) throw new Error('Counter unavailable');
        const doc = new DOMParser().parseFromString(await response.text(),'text/html');
        this.applySnapshot(doc.querySelector('eos-stories-counter'));
      } catch (_) {
        // Keep the last verified number and its timestamp on a failed refresh.
      } finally {
        clearTimeout(timeout);
        this._busy = false;
        this.schedule();
      }
    }
  });
})();
